import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Badge } from '@components/atoms/badge';
import { Button } from '@components/atoms/button';
import { Typography } from '@components/atoms/typography';
import { getFileExtensionsFromMimeType } from '@components/organisms/forms/form-upload-field/utils/get-file-extensions-from-mime-type';
import { FORM_MAX_LENGTH } from '@constants';
import { useScrollToError } from '@hooks/use-scroll-to-error';
import { BadgeStatusEnum, ManualFieldNamesEnum } from '@models/enums';
import type { AutocompleteOption } from '@models/types';
import type { CustomManualFieldModel } from '@models/verification';
import { CustomManualFieldType } from '@models/verification';
import { usePageTitle } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-page-title';
import { MobileHeader } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/mobile-header';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { STEPS_MOBILE_TITLE } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/constants';
import { CustomField } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/custom-field';
import { usePersonalData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/hooks/use-personal-data';
import { MainField } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/main-field';
import { PersonalDataStepContainer } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/styles';
import type {
    CustomFieldData,
    MainFieldData,
    PersonalDataFormValues,
    PersonalDataProps,
} from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/types';
import {
    useSetManualFieldsSDKMutation,
    useUploadCustomFileSDKMutation,
} from '@modules/docs-verification-widget/api';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '@store';
import { docsVerificationSlice } from '@store/slices/docs-verification';
import { useAppTheme } from '@theme';
import { formatDate } from '@utils/date/util';
import { getFileExtensions } from '@utils/files/util';
import { validateEmail } from '@utils/validation';
import { formResolver } from '@utils/yup/resolver';
import type { AnySchema } from 'yup';
import * as yup from 'yup';

export const PersonalData: FC<PersonalDataProps> = ({ onSubmit }) => {
    const { scrollTop } = useWidgetContext();
    const theme = useAppTheme();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const { data: personalDataFormValues, setData: setPersonalData } = usePersonalData();
    const [setManualFields] = useSetManualFieldsSDKMutation();
    const [uploadCustomField] = useUploadCustomFileSDKMutation();
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState('');
    const { pageTitle } = usePageTitle();

    const handleSubmit = useCallback(
        async (formValues: PersonalDataFormValues) => {
            if (!sdkOptions) {
                return;
            }

            try {
                setIsLoading(true);
                const body = {
                    ...formValues,
                    ...(formValues.dob
                        ? {
                              dob: formatDate(formValues.dob),
                          }
                        : {}),
                    ...(formValues.gender
                        ? {
                              gender: formValues.gender.value,
                          }
                        : {}),
                    ...(formValues.citizenship
                        ? {
                              citizenship: formValues.citizenship.value,
                          }
                        : {}),
                    ...(formValues.country
                        ? {
                              country: formValues.country.value,
                          }
                        : {}),
                    ...(formValues.custom_fields?.length &&
                    !!sdkOptions?.settings?.manual_fields_settings?.custom_fields?.length
                        ? {
                              custom_fields: formValues.custom_fields.reduce((acc, field) => {
                                  if (field.value instanceof File) {
                                      return acc;
                                  }
                                  const autocompleteValue = (field.value as AutocompleteOption)
                                      ?.value;

                                  // @ts-expect-error valid
                                  acc[field.label] = autocompleteValue || field.value;

                                  return acc;
                              }, {}),
                          }
                        : {
                              custom_fields: undefined,
                          }),
                };

                const uploadCustomRequests =
                    formValues.custom_fields
                        ?.map((field) => {
                            if (field.value instanceof File) {
                                const uploadedField = personalDataFormValues?.custom_fields?.find(
                                    (customField) => customField.label === field.label,
                                );

                                if (uploadedField?.value === field.value) {
                                    return null;
                                }
                                const formData = new FormData();

                                formData.append(field.label, field.value);

                                return uploadCustomField({
                                    url: sdkOptions.apiUrl,
                                    id: sdkOptions.id,
                                    body: formData,
                                }).unwrap();
                            }

                            return null;
                        })
                        .filter((item) => !!item) || [];

                await Promise.all([
                    setManualFields({
                        url: sdkOptions.apiUrl,
                        id: sdkOptions.id,
                        body,
                    }).unwrap(),
                    ...uploadCustomRequests,
                ]);

                setPersonalData(formValues);
                onSubmit();
            } catch (e) {
                setError(e?.data?.message || t('common.failedTryAgain'));
            } finally {
                setIsLoading(false);
            }
        },
        [onSubmit, setManualFields, uploadCustomField, sdkOptions, t, setPersonalData, personalDataFormValues],
    );

    const manualFields = sdkOptions?.settings?.manual_fields_settings;
    const isUseCustomFields = !!sdkOptions?.settings?.manual_fields_settings?.custom_fields;

    const mainFields = useMemo(() => {
        const fields: MainFieldData[] = [
            {
                name: ManualFieldNamesEnum.FULL_NAME,
                meta: manualFields?.full_name,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.EMAIL,
                meta: manualFields?.email,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.PHONE,
                meta: manualFields?.phone,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.COUNTRY,
                meta: manualFields?.country,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.STATE,
                meta: manualFields?.state,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.CITY,
                meta: manualFields?.city,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.ADDRESS_LINE1,
                meta: manualFields?.address_line1,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.ADDRESS_LINE2,
                meta: manualFields?.address_line2,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.ZIP,
                meta: manualFields?.zip,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.SSN,
                meta: manualFields?.ssn,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.DOB,
                meta: manualFields?.dob,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.GENDER,
                meta: manualFields?.gender,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.CITIZENSHIP,
                meta: manualFields?.citizenship,
                isCustom: false,
            },
            {
                name: ManualFieldNamesEnum.ADDRESS,
                meta: manualFields?.address,
                isCustom: false,
            },
        ].filter((field) => !!field?.meta?.enabled);

        return fields;
    }, [manualFields]);

    const methods = useForm<PersonalDataFormValues>({
        defaultValues: personalDataFormValues,
        resolver: formResolver(
            yup.object().shape({
                ...mainFields.reduce(
                    (acc, field) => {
                        switch (field.name) {
                            case ManualFieldNamesEnum.COUNTRY:
                            case ManualFieldNamesEnum.GENDER:
                            case ManualFieldNamesEnum.CITIZENSHIP:
                                acc[field.name] = yup
                                    .object()
                                    .typeError(t('validation.thisFieldIsRequired'))
                                    .required(t('validation.thisFieldIsRequired'));
                                break;

                            case ManualFieldNamesEnum.DOB:
                                acc[field.name] = yup
                                    .date()
                                    .required(t('validation.thisFieldIsRequired'))
                                    .typeError(t('validation.valueMustBeDate'));
                                break;

                            case ManualFieldNamesEnum.STATE:
                                acc[field.name] = yup
                                    .string()
                                    .required(t('validation.thisFieldIsRequired'))
                                    .matches(/^[A-Za-z\s]+$/, t('validation.valueMustBeLetters'));
                                break;

                            case ManualFieldNamesEnum.SSN:
                                acc[field.name] = yup
                                    .string()
                                    .required(t('validation.thisFieldIsRequired'))
                                    .matches(/^\d+$/, t('validation.valueMustBeNumber'))
                                    .min(
                                        FORM_MAX_LENGTH.SSN,
                                        t('validation.minLength', { value: FORM_MAX_LENGTH.SSN }),
                                    );
                                break;

                            case ManualFieldNamesEnum.ZIP:
                                acc[field.name] = yup
                                    .string()
                                    .required(t('validation.thisFieldIsRequired'))
                                    .matches(/^\d{5}(-\d{4})?$/, t('validation.invalidZipFormat'));
                                break;

                            default: {
                                let validation = yup
                                    .string()
                                    .required(t('validation.thisFieldIsRequired'))
                                    .test(
                                        'len',
                                        t('validation.maxLength', {
                                            value: FORM_MAX_LENGTH.DEFAULT,
                                        }),
                                        (val) => (val || '').length < FORM_MAX_LENGTH.DEFAULT,
                                    );

                                if (field.name === ManualFieldNamesEnum.EMAIL) {
                                    validation = validation.test(
                                        'emailTest',
                                        t('validation.invalidEmail'),
                                        (value) => {
                                            if (value === '' || value === undefined) {
                                                return true;
                                            }

                                            return validateEmail(value);
                                        },
                                    );
                                }

                                acc[field.name] = validation;
                                break;
                            }
                        }

                        return acc;
                    },
                    {} as Record<ManualFieldNamesEnum, AnySchema>,
                ),
                ...(isUseCustomFields
                    ? {
                          custom_fields: yup.array(
                              yup.object({
                                  label: yup.string(),
                                  caption: yup.string(),
                                  order: yup.number(),
                                  value: yup.mixed().test('len', (value, context) => {
                                      const { options } = context.parent;

                                      if (!value) {
                                          return context.createError({
                                              message: t('validation.thisFieldIsRequired'),
                                          });
                                      }

                                      if (options?.type === CustomManualFieldType.FILE) {
                                          const file = value as File;

                                          if (options.max_size && file.size > options.max_size) {
                                              return context.createError({
                                                  message: t('validation.fileTooBig'),
                                              });
                                          }

                                          if (options.allowed_mime_types) {
                                              const allowedExtensions = (
                                                  options.allowed_mime_types as string[]
                                              ).reduce<string[]>((acc, type) => {
                                                  const extensions =
                                                      getFileExtensionsFromMimeType(type);

                                                  acc.push(...extensions);

                                                  return acc;
                                              }, []);

                                              const extension = getFileExtensions(file);

                                              if (!allowedExtensions.includes(extension)) {
                                                  return context.createError({
                                                      message: t('validation.invalidFileExtension'),
                                                  });
                                              }
                                          }

                                          return true;
                                      }

                                      if (options?.type === CustomManualFieldType.SELECT) {
                                          return true;
                                      }

                                      return ((value as string) || '').length <
                                          FORM_MAX_LENGTH.DEFAULT
                                          ? true
                                          : context.createError({
                                                message: t('validation.maxLength', {
                                                    value: FORM_MAX_LENGTH.DEFAULT,
                                                }),
                                            });
                                  }),
                              }),
                          ),
                      }
                    : {}),
            }),
        ),
        ...(isUseCustomFields
            ? {
                  defaultValues: {
                      custom_fields:
                          sdkOptions?.settings?.manual_fields_settings?.custom_fields?.map(
                              (field, index) => ({
                                  label: field.label,
                                  caption: field.caption,
                                  order: field.order,
                                  index,
                                  value: null,
                                  isCustom: true,
                                  ...(field.options ? { options: field.options } : {}),
                              }),
                          ),
                  },
              }
            : {}),
    });

    const { fields: customFields } = useFieldArray<PersonalDataFormValues, 'custom_fields'>({
        control: methods.control,
        name: 'custom_fields',
    });

    useEffect(() => {
        if (personalDataFormValues) {
            methods.reset({
                ...personalDataFormValues,
                ...(personalDataFormValues.dob
                    ? {
                          dob: new Date(personalDataFormValues.dob),
                      }
                    : {}),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methods.reset]);

    useScrollToError({
        errors: methods.formState.errors,
        submitCount: methods.formState.submitCount,
    });

    const handleBackClick = () => {
        dispatch(docsVerificationSlice.actions.goBackStep());
        setTimeout(() => {
            scrollTop();
        });
    };

    const stepsMobileTitleNode = window.document.getElementById(STEPS_MOBILE_TITLE);

    const allFields = useMemo(() => {
        const fields = [...mainFields, ...customFields];

        fields.sort((a, b) => {
            const aOrder = a.isCustom
                ? (a as CustomManualFieldModel).order
                : (a as MainFieldData)?.meta?.order;
            const bOrder = b.isCustom
                ? (b as CustomManualFieldModel).order
                : (b as MainFieldData)?.meta?.order;

            return (aOrder || 0) - (bOrder || 0);
        });

        return fields;
    }, [customFields, mainFields]);

    return (
        <PersonalDataStepContainer>
            {matchesMobileSm &&
                stepsMobileTitleNode &&
                createPortal(
                    <React.Fragment>
                        <MobileHeader onBack={handleBackClick} title={pageTitle} />
                    </React.Fragment>,
                    stepsMobileTitleNode,
                )}
            <Typography variant='text-bold' color='text-light' className='title'>
                {manualFields?.description || t('docVerificationWidget.personalData.text')}
            </Typography>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(handleSubmit)} className='form'>
                    {allFields.map((field) => {
                        if (field.isCustom) {
                            const customField = field as CustomFieldData;

                            return <CustomField key={customField.id} field={customField} />;
                        }

                        return <MainField key={field.name} field={field} />;
                    })}
                    {!!error && <Badge status={BadgeStatusEnum.ERROR} text={error} />}
                    <Button variant='primary' size='xl' type='submit' loading={isLoading}>
                        {t('common.continue')}
                    </Button>
                </form>
            </FormProvider>
        </PersonalDataStepContainer>
    );
};
